import React, { useState } from 'react';
import {
  AlignedInfo,
  Button,
  ButtonGroup,
  Divider,
  Drawer,
  ErrorHandler,
  Gap,
  Gutter,
  StatusPill,
  Tag,
  Text,
} from '@common/components';
import { useQuery } from '@common/hooks';
import { useFeatureFlags } from '@common/contexts/feature-flags';
import { todayIso } from '@common/services/datetime';
import {
  LOAD_BALANCING_WORKER_QUERY,
  type LoadBalancingWorkerType,
} from '@load-balancing/graphql/queries/load-balancing-worker';
import { SCHEDULE_GROUP_TEAM_FILTERS_QUERY } from '@load-balancing/graphql';
import {
  employmentLabel,
  ftePercentageLabel,
  schedulingStatusLabel,
  shiftPatternLabel,
  shiftPreferenceLabel,
  toTeamReviewWorker,
} from '@load-balancing/services';
import {
  ScheduleWorkerProfileUpdateForm,
  useScheduleWorkerProfileUpdateForm,
  ScheduleWorkerSkills,
  ScheduleWorkerUpcomingChanges,
} from '@load-balancing/components';
import { useSkillCoverageEnabled } from '@load-balancing/hooks';
import { DEFAULT_CHARGE_ROLE_TERM } from '@load-balancing/types';

export function ScheduleTeamReviewWorkerDrawer({
  open,
  onClose,
  workerId,
  scheduleGroupId,
  schedulingPeriodId,
}: {
  open: boolean;
  onClose: () => void;
  workerId?: string;
  scheduleGroupId?: string;
  schedulingPeriodId?: string;
  asOf?: string;
}) {
  const [editTarget, setEditTarget] = useState<string | 'new' | null>(null);
  const { isSchedulingIncludeExcludeEnabled } = useFeatureFlags();
  const skillsEnabled = useSkillCoverageEnabled(scheduleGroupId);

  const { data, loading, error } = useQuery(LOAD_BALANCING_WORKER_QUERY, {
    variables: {
      id: workerId ?? '',
      scheduleGroupId: scheduleGroupId ?? '',
      schedulingPeriodId,
    },
    skip: !workerId || !scheduleGroupId,
  });

  const { data: filtersData, error: filtersError } = useQuery(
    SCHEDULE_GROUP_TEAM_FILTERS_QUERY,
    {
      variables: { scheduleGroupId: scheduleGroupId ?? '' },
      skip: !scheduleGroupId,
    }
  );
  const assignableRoles = filtersData?.scheduleGroupTeamFilters?.roles ?? [];
  const chargeRole =
    filtersData?.scheduleGroupTeamFilters?.chargeRole ?? undefined;
  const chargeRoleTerm =
    filtersData?.scheduleGroupTeamFilters?.chargeRoleTerm ??
    DEFAULT_CHARGE_ROLE_TERM;

  if (error || filtersError) {
    return (
      <ErrorHandler
        error={error || filtersError}
        log="ScheduleTeamReviewWorkerDrawer"
        messageType="contextual"
      />
    );
  }

  const asOf = todayIso();
  const loadBalancingWorker = data?.loadBalancingWorker;
  const canManageProfile =
    loadBalancingWorker?.homeUnit?.canManageWorkerProfile.value ?? false;

  const handleClose = () => {
    setEditTarget(null);
    onClose();
  };

  if (
    editTarget &&
    loadBalancingWorker &&
    scheduleGroupId &&
    canManageProfile
  ) {
    return (
      <WorkerProfileEditPanel
        open={open}
        onClose={handleClose}
        worker={loadBalancingWorker}
        scheduleGroupId={scheduleGroupId}
        asOf={asOf}
        schedulingPeriodId={schedulingPeriodId}
        assignableRoles={assignableRoles}
        chargeRole={chargeRole}
        skillsEnabled={skillsEnabled}
        chargeRoleTerm={chargeRoleTerm}
        editingChangeId={editTarget === 'new' ? null : editTarget}
        onDone={() => setEditTarget(null)}
      />
    );
  }

  const worker = loadBalancingWorker
    ? toTeamReviewWorker(loadBalancingWorker, asOf)
    : undefined;

  const chargeEligible = worker?.chargeEligible ? 'Yes' : '—';

  const statusTags = worker && (
    <StatusTags
      key="status"
      status={worker.status}
      employment={employmentLabel(worker.employmentType)}
      enabled={isSchedulingIncludeExcludeEnabled}
    />
  );

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      loading={loading}
      title={
        worker ? <Text transform="capitalize">{worker.name}</Text> : undefined
      }
      footer={
        worker &&
        canManageProfile && (
          <Button kind="primary" onClick={() => setEditTarget('new')}>
            Update Profile
          </Button>
        )
      }
    >
      {worker && (
        <>
          <Gutter direction="bottom" size="sm">
            <Text size="lg" weight="medium" block>
              Profile
            </Text>
          </Gutter>
          <AlignedInfo
            fullWidth
            content={[
              [
                isSchedulingIncludeExcludeEnabled
                  ? 'Scheduling Status'
                  : 'Status',
                statusTags,
              ],
              ...(isSchedulingIncludeExcludeEnabled
                ? [['Employment Type', employmentLabel(worker.employmentType)]]
                : []),
              ['Unit', worker.unit ?? '—'],
              ['Role', worker.role ?? '—'],
              [`${chargeRoleTerm} Eligible`, chargeEligible],
              ['FTE', ftePercentageLabel(worker.ftePercentage)],
            ]}
          />

          {skillsEnabled && (
            <>
              <Divider secondary gutter={24} />

              <Gutter direction="bottom" size="sm">
                <Text size="lg" weight="medium" block>
                  Skills
                </Text>
              </Gutter>
              <ScheduleWorkerSkills
                workerId={workerId ?? ''}
                scheduleGroupId={scheduleGroupId ?? ''}
                asOf={asOf}
              />
            </>
          )}

          <Divider secondary gutter={24} />

          <Gutter direction="bottom" size="sm">
            <Text size="lg" weight="medium" block>
              Preferences
            </Text>
          </Gutter>
          <AlignedInfo
            fullWidth
            content={[
              ['Shift', shiftPreferenceLabel(worker.shiftType)],
              ['Shift Pattern', shiftPatternLabel(worker.shiftPattern)],
            ]}
          />

          <Divider secondary gutter={24} />

          <Gutter direction="bottom" size="md">
            <Text size="lg" weight="medium" block>
              Upcoming Changes
            </Text>
          </Gutter>
          <ScheduleWorkerUpcomingChanges
            changes={worker.upcomingChanges}
            workerId={workerId ?? ''}
            scheduleGroupId={scheduleGroupId ?? ''}
            schedulingPeriodId={schedulingPeriodId}
            onEdit={setEditTarget}
            canManageProfile={canManageProfile}
            skillsEnabled={skillsEnabled}
            chargeRoleTerm={chargeRoleTerm}
          />
        </>
      )}
    </Drawer>
  );
}

function WorkerProfileEditPanel({
  open,
  onClose,
  worker,
  scheduleGroupId,
  asOf,
  schedulingPeriodId,
  assignableRoles,
  chargeRole,
  skillsEnabled,
  chargeRoleTerm,
  editingChangeId,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  worker: LoadBalancingWorkerType;
  scheduleGroupId: string;
  asOf: string;
  schedulingPeriodId?: string;
  assignableRoles: readonly string[];
  chargeRole?: string;
  skillsEnabled: boolean;
  chargeRoleTerm: string;
  editingChangeId: string | null;
  onDone: () => void;
}) {
  const form = useScheduleWorkerProfileUpdateForm({
    workerId: worker.id,
    scheduleGroupId,
    terms: worker.terms,
    asOf,
    schedulingPeriodId,
    editingTermId: editingChangeId,
    workerName: worker.name,
    skillsEnabled,
    onSaved: onDone,
  });
  const { isSchedulingIncludeExcludeEnabled } = useFeatureFlags();

  const formEl = (
    <ScheduleWorkerProfileUpdateForm
      values={form.values}
      onChange={form.update}
      saveAttempted={form.saveAttempted}
      serverErrors={form.serverErrors}
      datesDisabled={editingChangeId !== null}
      assignableRoles={assignableRoles}
      chargeRole={chargeRole}
      scheduleGroupId={scheduleGroupId}
      skillsEnabled={skillsEnabled}
      skillsLocked={form.skillsLocked}
      loadedSkillUuids={form.loadedSkillUuids}
      skillsError={form.skillsError}
      chargeRoleTerm={chargeRoleTerm}
    />
  );
  const buttons = (
    <ButtonGroup align="right">
      <Button kind="secondary" onClick={onDone}>
        Cancel
      </Button>
      <Button kind="primary" loading={form.saving} onClick={form.save}>
        Save
      </Button>
    </ButtonGroup>
  );

  if (!isSchedulingIncludeExcludeEnabled) {
    return (
      <Drawer open={open} onClose={onClose} title="Update Profile">
        <Gap vertical size="lg" align="stretch">
          {formEl}
          {buttons}
        </Gap>
      </Drawer>
    );
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Update Profile"
      footer={buttons}
    >
      {formEl}
    </Drawer>
  );
}

function StatusTags({
  status,
  employment,
  enabled,
}: {
  status: 'active' | 'inactive';
  employment: string;
  enabled: boolean;
}) {
  const pill = (
    <StatusPill variant={status === 'active' ? 'success' : 'neutral'}>
      <Text weight="medium">
        {schedulingStatusLabel(status === 'active', enabled)}
      </Text>
    </StatusPill>
  );

  if (enabled) return pill;

  return (
    <Gap key="status" size="xs" align="center">
      {pill}
      <Tag color="steelLighter" kind="square">
        <Text weight="medium">{employment}</Text>
      </Tag>
    </Gap>
  );
}
